/**
 * Tracking-context: UTM/referrer/landing_url, привязанные к сессии.
 *
 * Логика: при первой посадке на любую страницу сайта `<plp-form>` зовёт
 * `snapshot()` — UTM из URL (если есть) и referrer запоминаются в localStorage
 * на всю сессию (sessionStorage используется как маркер «уже сняли»).
 * При submit формы `<plp-form>` зовёт `read()` и прикладывает к payload.
 *
 * Так UTM из первой посадки доезжают в CRM даже если юзер перешёл по
 * внутренним ссылкам и сабмитнул на 4-й странице.
 */

const STORAGE_KEY = '__plp_tracking';
const SESSION_KEY = '__plp_session_snapshotted';

export interface Tracking {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_url?: string;
  captured_at?: string;
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

function safeStorage<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function hasMeaningfulData(t: Tracking): boolean {
  return Boolean(
    t.utm_source || t.utm_medium || t.utm_campaign || t.utm_content || t.utm_term || t.referrer
  );
}

export function snapshot(): void {
  // Уже снимали в этой сессии — не перезаписываем (берём first-touch).
  const alreadyDone = safeStorage(() => sessionStorage.getItem(SESSION_KEY), null);
  if (alreadyDone === '1' && safeStorage(() => localStorage.getItem(STORAGE_KEY), null)) {
    return;
  }

  const params = new URLSearchParams(location.search);
  const t: Tracking = {
    captured_at: new Date().toISOString(),
    landing_url: location.href,
  };
  if (document.referrer && !document.referrer.startsWith(location.origin)) {
    t.referrer = document.referrer;
  }
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) (t as Record<string, string>)[key] = v;
  }

  if (!hasMeaningfulData(t)) {
    // Нет ни UTM, ни внешнего referrer — нечего сохранять, чтобы не затирать
    // существующий снапшот пустыми значениями.
    safeStorage(() => sessionStorage.setItem(SESSION_KEY, '1'), undefined);
    return;
  }

  safeStorage(() => localStorage.setItem(STORAGE_KEY, JSON.stringify(t)), undefined);
  safeStorage(() => sessionStorage.setItem(SESSION_KEY, '1'), undefined);
}

export function read(): Tracking {
  const raw = safeStorage(() => localStorage.getItem(STORAGE_KEY), null);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as Tracking;
  } catch {
    /* ignore corrupt storage */
  }
  return {};
}

export function clear(): void {
  safeStorage(() => localStorage.removeItem(STORAGE_KEY), undefined);
  safeStorage(() => sessionStorage.removeItem(SESSION_KEY), undefined);
}
